import { useEffect, useState } from 'react';
import { Play, SkipForward, Trophy, RotateCcw, ArrowUp, ArrowDown, Share2 } from 'lucide-react';
import QRShareModal from '@/components/QRShareModal';
import { buildExerciseShareUrl } from '@/lib/tuningShare';
import { ResetStatsButton } from '@/components/tuner/ResetStatsButton';
import { MiniIntervalTuner } from '@/components/tuner/MiniIntervalTuner';
import { TuningSlide } from '@/components/tuner/TuningSlide';
import { toast } from '@/components/ui/sonner';
import { Switch } from '@/components/ui/switch';
import { INTERVALS, INTERVAL_SHORT, pitchClass } from '@/lib/pitch';
import { useIntervalRound, ROUND } from '@/hooks/useIntervalRound';

const CHOICES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const DIRECTIONS = [{ v: 'up', label: 'Sopra' }, { v: 'down', label: 'Sotto' }, { v: 'mixed', label: 'Misti' }];
const shortOf = (s) => (s === 12 ? 'P8' : INTERVAL_SHORT[s]);
const nameOf = (s) => (s === 12 ? 'Octave' : INTERVALS[s]);
const loadJson = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k) || 'null'); return v ?? d; } catch { return d; } };
const loadSel = () => { const v = loadJson('tuner.intervals', null); return Array.isArray(v) && v.length ? v : [3, 4, 5, 7, 12]; };
const scoreColor = (s) => (s >= 80 ? 'text-emerald-300' : s >= 50 ? 'text-sky-300' : 'text-pink-300');
const Arrow = ({ dir, className }) => (dir > 0 ? <ArrowUp className={className} /> : <ArrowDown className={className} />);

const IntervalPicker = ({ selected, onToggle, disabled, usable }) => (
  <div className="grid grid-cols-6 gap-1.5">
    {CHOICES.map((s) => (
      <button
        key={s}
        data-testid={`interval-choice-${s}`}
        data-usable={usable(s)}
        disabled={disabled}
        onClick={() => onToggle(s)}
        className={`h-10 rounded-xl font-mono text-sm transition-[background-color,opacity] disabled:opacity-50 ${selected.includes(s) ? 'bg-pink-400 text-black' : 'border border-white/10 text-white/70 hover:bg-white/5'} ${usable(s) ? '' : 'opacity-30 line-through'}`}
      >
        {shortOf(s)}
      </button>
    ))}
  </div>
);

const Options = ({ direction, setDirection, timed, setTimed, barsPer, setBarsPer, inScale, setInScale, scale, disabled }) => (
  <div className="flex flex-col gap-3">
    <div className="grid grid-cols-3 gap-1.5">
      {DIRECTIONS.map((d) => (
        <button key={d.v} data-testid={`interval-dir-${d.v}`} disabled={disabled} onClick={() => setDirection(d.v)} className={`h-10 rounded-xl text-[11px] uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${direction === d.v ? 'bg-sky-400 text-black' : 'border border-white/10 text-white/70 hover:bg-white/5'}`}>{d.label}</button>
      ))}
    </div>
    <label className="flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-[0.25em] text-white/45">Solo intervalli della scala{scale ? ` · ${scale.name}` : ''}</span>
      <Switch data-testid="interval-in-scale" checked={inScale && !!scale} disabled={disabled || !scale} onCheckedChange={setInScale} />
    </label>
    {!scale && <span className="text-[11px] text-white/35 -mt-2">Scegli una scala nel riquadro "Scala sul bordone" per usare questa opzione.</span>}
    <label className="flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-[0.25em] text-white/45">A tempo con il click</span>
      <Switch data-testid="interval-timed" checked={timed} disabled={disabled} onCheckedChange={setTimed} />
    </label>
    {timed && (
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] uppercase tracking-[0.25em] text-white/45">Battute per intervallo</span>
        <div className="flex gap-1.5">
          {[1, 2, 4].map((n) => (
            <button key={n} data-testid={`interval-bars-${n}`} disabled={disabled} onClick={() => setBarsPer(n)} className={`h-9 w-11 rounded-lg font-mono text-sm transition-colors disabled:opacity-50 ${barsPer === n ? 'bg-pink-400 text-black' : 'border border-white/10 text-white/70 hover:bg-white/5'}`}>{n}</button>
          ))}
        </div>
      </div>
    )}
  </div>
);

const Prompt = ({ round, rootPc, flats, timed, barsPer, interval, pitch }) => {
  const t = round.targets[round.idx];
  const last = round.results[round.results.length - 1];
  const showLast = (timed ? round.idx > 0 : round.waiting) && last !== undefined;
  if (round.countdown) {
    return (
      <div data-testid="interval-countin" className="rounded-2xl border border-white/10 p-5 flex flex-col items-center gap-1">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Preparati</span>
        <span className="text-sm text-white/70">Il primo intervallo arriva alla prossima battuta</span>
      </div>
    );
  }
  return (
    <div data-testid="interval-prompt" className={`rounded-2xl border p-5 flex flex-col items-center gap-1 transition-colors ${round.waiting ? 'border-emerald-400/50 bg-emerald-400/5' : 'border-sky-400/40 bg-sky-400/5'}`}>
      <span data-testid="interval-progress" className="text-[10px] uppercase tracking-[0.3em] text-white/45">{round.idx + 1}/{ROUND}{timed ? (barsPer > 1 ? ` · battuta ${round.bar + 1}/${barsPer}` : ' · questa battuta') : ''}</span>
      <span className="flex items-center gap-2">
        <span data-testid="interval-target" className="font-mono font-semibold text-5xl">{shortOf(t.s)}</span>
        <Arrow dir={t.dir} className={`w-8 h-8 ${t.dir > 0 ? 'text-pink-300' : 'text-sky-300'}`} />
      </span>
      <span className="text-sm text-white/70">
        {nameOf(t.s)} <span data-testid="interval-target-dir">{t.dir > 0 ? 'sopra' : 'sotto'}</span> · suona{' '}
        <span data-testid="interval-target-note" className="font-bebas text-xl text-sky-300">{pitchClass((((rootPc + t.dir * t.s) % 12) + 12) % 12, flats)}</span>
      </span>
      <span data-testid="interval-last-score" className={`text-xs font-mono mt-1 h-4 ${showLast ? scoreColor(last) : 'text-white/40'}`}>
        {showLast ? `${timed ? 'Intervallo precedente: ' : ''}${last} punti` : 'Tieni la nota intonata…'}
      </span>
      <TuningSlide pitch={pitch} testid="interval-tuning-slide" />
      <MiniIntervalTuner interval={interval} pitch={pitch} target={t} />
    </div>
  );
};

const Result = ({ round, avg, best }) => (
  <div data-testid="interval-result" className="rounded-2xl border border-emerald-400/40 bg-emerald-400/5 p-5 flex flex-col items-center gap-2">
    <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Punteggio finale</span>
    <span data-testid="interval-final-score" className={`font-bebas text-6xl leading-none ${scoreColor(avg)}`}>{avg}</span>
    {avg >= best && avg > 0 && <span data-testid="interval-record" className="text-xs text-sky-300 uppercase tracking-[0.2em]">Nuovo record!</span>}
    <div className="flex flex-wrap justify-center gap-1.5 mt-1">
      {round.targets.map((t, i) => (
        <span key={i} className={`text-[10px] font-mono px-2 py-1 rounded-md border border-white/10 inline-flex items-center gap-0.5 ${scoreColor(round.results[i] ?? 0)}`}>
          {shortOf(t.s)}<Arrow dir={t.dir} className="w-3 h-3" /> {round.results[i] ?? 0}
        </span>
      ))}
    </div>
  </div>
);

export const IntervalTrainer = ({ drone, interval, pitch, flats, scale, onPlayingChange }) => {
  const [selected, setSelected] = useState(loadSel);
  const [direction, setDirection] = useState(() => loadJson('tuner.intervalDir', 'up'));
  const [timed, setTimed] = useState(() => loadJson('tuner.intervalTimed', false));
  const [barsPer, setBarsPer] = useState(() => loadJson('tuner.intervalBars', 1));
  const [inScale, setInScale] = useState(() => loadJson('tuner.intervalInScale', false));
  const [best, setBest] = useState(() => Number(localStorage.getItem('tuner.intervalBest') || 0));
  const scaleOn = inScale && !!scale;
  // Interval s in direction dir lands on a scale degree?
  const allowed = (s, dir) => !scaleOn || scale.steps.includes((((dir * s) % 12) + 12) % 12);
  const dirs = direction === 'down' ? [-1] : direction === 'up' ? [1] : [1, -1];
  const usable = (s) => dirs.some((d) => allowed(s, d));
  const { round, playing, start, stop, skip, stoppedByClick, poolSize, weak, resetHistory, hasHistory } = useIntervalRound({ interval, selected, direction, timed, barsPer, allowed });
  const resetStats = () => {
    resetHistory();
    setBest(0);
    localStorage.removeItem('tuner.intervalBest');
    toast.success('Statistiche azzerate');
  };
  const [sharing, setSharing] = useState(false);
  useEffect(() => { onPlayingChange?.(playing); }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { localStorage.setItem('tuner.intervals', JSON.stringify(selected)); }, [selected]);
  useEffect(() => { localStorage.setItem('tuner.intervalDir', JSON.stringify(direction)); }, [direction]);
  useEffect(() => { localStorage.setItem('tuner.intervalTimed', JSON.stringify(timed)); }, [timed]);
  useEffect(() => { localStorage.setItem('tuner.intervalBars', JSON.stringify(barsPer)); }, [barsPer]);
  useEffect(() => { localStorage.setItem('tuner.intervalInScale', JSON.stringify(inScale)); }, [inScale]);

  const toggleChoice = (s) => setSelected((prev) => (prev.includes(s) ? (prev.length > 1 ? prev.filter((x) => x !== s) : prev) : [...prev, s].sort((a, b) => a - b)));
  const begin = () => { if (!drone.active) drone.toggle(); start(); };

  const finished = round && round.idx >= ROUND;
  const avg = round?.results.length ? Math.round(round.results.reduce((a, b) => a + b, 0) / round.results.length) : 0;
  useEffect(() => {
    if (finished && avg > best) { setBest(avg); localStorage.setItem('tuner.intervalBest', String(avg)); }
  }, [finished]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section data-testid="interval-trainer" className="bg-[#111112] border border-white/5 rounded-2xl p-4 sm:p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">Esercizio intervalli sul bordone</span>
        <span data-testid="interval-best" className="text-xs font-mono text-white/50 inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-sky-300" /> {best}</span>
      </div>
      <IntervalPicker selected={selected} onToggle={toggleChoice} disabled={playing} usable={usable} />
      <Options direction={direction} setDirection={setDirection} timed={timed} setTimed={setTimed} barsPer={barsPer} setBarsPer={setBarsPer} inScale={inScale} setInScale={setInScale} scale={scale} disabled={playing} />
      {timed && !playing && <span className="text-[11px] text-white/40">Il punteggio conta tutte le battute dell'intervallo: tempo e BPM si regolano nel riquadro Click.</span>}
      {!poolSize && !playing && <span data-testid="interval-empty-pool" className="text-[11px] text-pink-300">Nessun intervallo scelto è nella scala in questa direzione: aggiungine altri.</span>}
      {stoppedByClick && <span data-testid="interval-click-stopped" className="text-[11px] text-pink-300">Round interrotto: il click è stato fermato.</span>}
      {!playing && weak.length > 0 && (
        <div data-testid="interval-weak" className="text-[11px] text-white/50 flex flex-wrap items-center gap-1.5">
          <span>Punti deboli, proposti più spesso:</span>
          {weak.map((t) => (
            <span key={`${t.dir}${t.s}`} className="font-mono px-1.5 py-0.5 rounded-md border border-pink-400/30 text-pink-300 inline-flex items-center gap-0.5">
              {shortOf(t.s)}<Arrow dir={t.dir} className="w-3 h-3" /> {t.avg}
            </span>
          ))}
        </div>
      )}

      {playing && <Prompt round={round} rootPc={drone.rootPc} flats={flats} timed={timed} barsPer={barsPer} interval={interval} pitch={pitch} />}
      {finished && <Result round={round} avg={avg} best={best} />}

      <div className="flex flex-wrap gap-2">
        {playing ? (
          <>
            {!timed && <button data-testid="interval-skip" disabled={round.waiting} onClick={skip} className="h-11 px-4 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-white/5 disabled:opacity-40"><SkipForward className="w-4 h-4" /> Salta</button>}
            <button data-testid="interval-stop" onClick={stop} className="h-11 px-4 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] hover:bg-white/5">Interrompi</button>
          </>
        ) : (
          <button data-testid="interval-start" disabled={!poolSize} onClick={begin} className="h-11 px-5 rounded-full bg-pink-400 text-black text-xs font-semibold uppercase tracking-[0.2em] inline-flex items-center gap-2 active:scale-95 transition-transform disabled:opacity-40">
            {finished ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" />} {finished ? 'Nuovo round' : 'Inizia round'}
          </button>
        )}
        {!playing && (
          <button data-testid="interval-share" onClick={() => setSharing(true)} className="h-11 px-4 rounded-full border border-sky-400/40 text-sky-300 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-sky-400/10">
            <Share2 className="w-4 h-4" /> Condividi
          </button>
        )}
        {!playing && (hasHistory || best > 0) && <ResetStatsButton onConfirm={resetStats} />}
      </div>
      <QRShareModal
        open={sharing}
        url={sharing ? buildExerciseShareUrl(drone.cfg, scale) : ''}
        title="Esercizio intervalli"
        subtitle={`${selected.map(shortOf).join(' ')} · ${DIRECTIONS.find((d) => d.v === direction).label}${timed ? ` · ${barsPer} batt.` : ''}`}
        caption="Scansiona per aprire l'esercizio completo"
        onClose={() => setSharing(false)}
      />
    </section>
  );
};
